/**
 * Welcome Email Service
 * Sends welcome email to new business owners with:
 * - Welcome message
 * - Widget installation instructions
 * - App Store and Play Store links
 * - QR codes for app downloads
 */

interface BusinessOwner {
  id: string;
  email: string;
  businessName: string;
  name: string;
}

interface WelcomeEmailData {
  businessOwner: BusinessOwner;
  widgetCode: string;
  appStoreUrl: string;
  playStoreUrl: string;
  webAppUrl: string;
}

class WelcomeEmailService {
  private readonly API_BASE_URL = __DEV__
    ? 'http://localhost:3000/api'
    : 'https://bookapt-payment-api.herokuapp.com/api';
  
  // App Store and Play Store URLs (update with actual URLs when apps are published)
  private readonly APP_STORE_URL = 'https://apps.apple.com/app/bookapt/id123456789'; // Update with actual App Store URL
  private readonly PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bookapt.app'; // Update with actual Play Store URL
  private readonly WEB_APP_URL = __DEV__
    ? 'http://localhost:3001'
    : 'https://app.bookapt.io';

  /**
   * Send welcome email to new business owner
   */
  async sendWelcomeEmail(businessOwner: BusinessOwner): Promise<boolean> {
    try {
      console.log('📧 Sending welcome email to:', businessOwner.email);

      // Generate widget code
      const widgetCode = this.generateWidgetCode(businessOwner.id);

      // Get app store URLs
      const appStoreUrl = this.APP_STORE_URL;
      const playStoreUrl = this.PLAY_STORE_URL;
      const webAppUrl = this.WEB_APP_URL;

      const emailData: WelcomeEmailData = {
        businessOwner,
        widgetCode,
        appStoreUrl,
        playStoreUrl,
        webAppUrl,
      };

      // Send email via backend API
      const response = await fetch(`${this.API_BASE_URL}/email/send-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Welcome email sent successfully');
        return true;
      } else {
        console.error('❌ Failed to send welcome email:', result.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending welcome email:', error);
      return false;
    }
  }

  /**
   * Generate widget installation code for business
   */
  private generateWidgetCode(businessId: string): string {
    return `<script src="https://bookapt-payment-api.onrender.com/widget/bookapt-widget.js" data-business-id="${businessId}" async></script>`;
  }

  /**
   * Generate QR code data URL for App Store
   */
  generateAppStoreQRCode(appStoreUrl: string): string {
    // Use a QR code service (e.g., qrcode.js, qrcode-generator)
    // For now, return a placeholder - you'll need to implement actual QR code generation
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(appStoreUrl)}`;
  }

  /**
   * Generate QR code data URL for Play Store
   */
  generatePlayStoreQRCode(playStoreUrl: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(playStoreUrl)}`;
  }
}

export const welcomeEmailService = new WelcomeEmailService();
export default welcomeEmailService;

